import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { openAiApiService } from "../../../cores/openAi/services/api";
import { SocialTemplate } from "../../../cores/openAi/interface/api/get-social-templates";
import { aiEmailToHtml, EmailAiTemplate } from "./emailHtmlUtils";

interface EmailAiWriterProps {
  companyId?: string;
  onApply: (subject: string, html: string) => void;
}

const mapTemplates = (templates: SocialTemplate[]): EmailAiTemplate[] =>
  templates.map((template) => ({
    subject: template.subject || template.title || "",
    body: template.body || "",
    callToAction: (template as EmailAiTemplate).callToAction,
  }));

export const EmailAiWriter = ({ companyId, onApply }: EmailAiWriterProps) => {
  const intl = useIntl();
  const [industry, setIndustry] = useState("");
  const [purpose, setPurpose] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [emoticons, setEmoticons] = useState(true);
  const [hashtags, setHashtags] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<EmailAiTemplate[]>([]);
  const [selected, setSelected] = useState(0);

  const mapGenerationError = useCallback(
    (error: any) => {
      const status = error?.response?.status || error?.request?.status;
      const responseMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message;

      if (status === 403) {
        return intl.formatMessage({
          id: "app.emailMarketing.editor.aiErrorForbidden",
        });
      }

      return (
        responseMessage ||
        intl.formatMessage({ id: "app.emailMarketing.editor.aiErrorGeneric" })
      );
    },
    [intl]
  );

  const handleGenerate = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!companyId) {
        toast.error(
          intl.formatMessage({ id: "app.emailMarketing.editor.aiErrorCompany" })
        );
        return;
      }

      if (!industry.trim() || !purpose.trim() || !additionalInfo.trim()) {
        toast.error(
          intl.formatMessage({ id: "app.emailMarketing.editor.aiErrorFields" })
        );
        return;
      }

      setLoading(true);
      try {
        const response = await openAiApiService.getEmailPosts(companyId, {
          industry: industry.trim(),
          subject: purpose.trim(),
          emoticons,
          hashtags,
          additionalInfo: additionalInfo.trim(),
          socialMedias: ["Email Marketing"],
        });

        const generated = mapTemplates(response);
        if (!generated.length) {
          throw new Error(
            intl.formatMessage({ id: "app.emailMarketing.editor.aiErrorEmpty" })
          );
        }

        setTemplates(generated);
        setSelected(0);
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.editor.aiSuccess" })
        );
      } catch (error: any) {
        toast.error(mapGenerationError(error));
      } finally {
        setLoading(false);
      }
    },
    [
      companyId,
      industry,
      purpose,
      additionalInfo,
      emoticons,
      hashtags,
      intl,
      mapGenerationError,
    ]
  );

  const handleApply = useCallback(() => {
    const template = templates[selected];
    if (!template) {
      return;
    }

    const subject =
      template.subject ||
      intl.formatMessage({ id: "app.emailMarketing.editor.defaultSubject" });
    const html = aiEmailToHtml(template.body, template.callToAction);
    onApply(subject, html);
    toast.success(
      intl.formatMessage({ id: "app.emailMarketing.editor.aiApplied" })
    );
  }, [templates, selected, onApply, intl]);

  const current = templates[selected];

  return (
    <div className="email-mkt__ai-writer">
      <div className="email-mkt__ai-writer-header">
        <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"]} />
        <h3>
          <FormattedMessage id="app.emailMarketing.editor.aiTitle" />
        </h3>
      </div>
      <p className="email-mkt__copy">
        <FormattedMessage id="app.emailMarketing.editor.aiHelp" />
      </p>

      <form className="email-mkt__ai-form" onSubmit={handleGenerate}>
        <label className="email-mkt__field">
          <span>
            <FormattedMessage id="app.openAiForm.industry" />
          </span>
          <input
            value={industry}
            onChange={(event) => setIndustry(event.target.value)}
            placeholder={intl.formatMessage({ id: "app.openAiForm.industry" })}
          />
        </label>
        <label className="email-mkt__field">
          <span>
            <FormattedMessage id="app.openAiForm.purpose" />
          </span>
          <input
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            placeholder={intl.formatMessage({ id: "app.openAiForm.purpose" })}
          />
        </label>
        <label className="email-mkt__field">
          <span>
            <FormattedMessage id="app.openAiForm.additionalInfo" />
          </span>
          <textarea
            rows={3}
            value={additionalInfo}
            onChange={(event) => setAdditionalInfo(event.target.value)}
            placeholder={intl.formatMessage({
              id: "app.openAiForm.additionalInfo",
            })}
          />
        </label>

        <div className="email-mkt__ai-checks">
          <label>
            <input
              type="checkbox"
              checked={emoticons}
              onChange={(event) => setEmoticons(event.target.checked)}
            />
            <FormattedMessage id="app.emailMarketing.editor.aiEmoticons" />
          </label>
          <label>
            <input
              type="checkbox"
              checked={hashtags}
              onChange={(event) => setHashtags(event.target.checked)}
            />
            <FormattedMessage id="app.emailMarketing.editor.aiHashtags" />
          </label>
        </div>

        <button
          type="submit"
          className="email-mkt__primary-btn email-mkt__ai-generate-btn"
          disabled={loading}
        >
          {loading ? (
            <FormattedMessage id="app.emailMarketing.editor.aiGenerating" />
          ) : (
            <FormattedMessage id="app.emailMarketing.editor.aiGenerate" />
          )}
        </button>
      </form>

      {current && (
        <div className="email-mkt__ai-results">
          <div className="email-mkt__ai-results-nav">
            <button
              type="button"
              className="email-mkt__ghost-btn"
              disabled={selected === 0}
              onClick={() => setSelected((value) => value - 1)}
            >
              <FontAwesomeIcon icon={["fas", "angle-left"]} />
            </button>
            <span>
              {selected + 1} / {templates.length}
            </span>
            <button
              type="button"
              className="email-mkt__ghost-btn"
              disabled={selected >= templates.length - 1}
              onClick={() => setSelected((value) => value + 1)}
            >
              <FontAwesomeIcon icon={["fas", "angle-right"]} />
            </button>
          </div>

          {current.subject && (
            <p className="email-mkt__ai-result-subject">
              <strong>
                <FormattedMessage id="app.emailMarketing.editor.subject" />:
              </strong>{" "}
              {current.subject}
            </p>
          )}
          <div className="email-mkt__ai-result-body">{current.body}</div>

          <button
            type="button"
            className="email-mkt__primary-btn"
            onClick={handleApply}
          >
            <FormattedMessage id="app.emailMarketing.editor.aiApply" />
          </button>
        </div>
      )}
    </div>
  );
};
